/**
 * Position solaire (SPEC.md §6, modèle A).
 *
 * Deux couches, qu'il ne faut jamais confondre :
 *
 * - **La géométrie** — `solarElevation` — est une identité trigonométrique.
 *   Elle est exacte, et ses bancs le sont par construction.
 * - **L'éphéméride** — `solarDeclination`, `equationOfTime` — est une
 *   approximation polynomiale du mouvement apparent du Soleil. Sa sortie est
 *   une **référence externe** au sens de SPEC §3.2 : elle peut servir d'entrée
 *   à un modèle, jamais de mesure d'arbitrage. Une prédiction confrontée à une
 *   éphéméride ne prouve rien — c'est un modèle qui en juge un autre.
 *
 * Formules : série basse précision de l'US Naval Observatory, annoncée à
 * ≈ 0,01° en déclinaison sur 1950-2050. Ce chiffre est une déclaration tant
 * qu'il n'a pas été confronté à une table de référence — voir la note finale de
 * `bench/solar.test.ts`.
 */

import { PI, asin, atan2, cos, floor, sin } from "./math.js";
import { daysSinceJ2000 } from "./time.js";
import type { Quantity } from "./units.js";
import { quantity, valueIn } from "./units.js";

/** Obliquité moyenne de l'écliptique à l'époque J2000. */
export const MEAN_OBLIQUITY = quantity(23.4393, "deg");

const DEG_TO_RAD = PI / 180;

function toRadians(degrees: number): number {
  return degrees * DEG_TO_RAD;
}

function toDegrees(radians: number): number {
  return radians / DEG_TO_RAD;
}

/** Ramène un angle en degrés dans [0, 360). */
function normalizeDegrees(degrees: number): number {
  return degrees - 360 * floor(degrees / 360);
}

/** Ramène un angle en degrés dans [−180, 180). */
function centerDegrees(degrees: number): number {
  return normalizeDegrees(degrees + 180) - 180;
}

/** Longitude moyenne du Soleil, en degrés. */
function meanLongitude(n: number): number {
  return normalizeDegrees(280.46 + 0.9856474 * n);
}

/** Anomalie moyenne du Soleil, en degrés. */
function meanAnomaly(n: number): number {
  return normalizeDegrees(357.528 + 0.9856003 * n);
}

/** Longitude écliptique apparente, en degrés. */
function eclipticLongitude(n: number): number {
  const g = toRadians(meanAnomaly(n));
  return normalizeDegrees(meanLongitude(n) + 1.915 * sin(g) + 0.02 * sin(2 * g));
}

/** Obliquité de l'écliptique à la date, en degrés. */
function obliquity(n: number): number {
  return valueIn(MEAN_OBLIQUITY, "deg") - 3.6e-7 * n;
}

/**
 * Déclinaison du Soleil — sa latitude céleste.
 *
 * **Référence externe** : sortie de modèle, jamais une observation.
 */
export function solarDeclination(julianDay: Quantity<"time">): Quantity<"angle"> {
  const n = valueIn(daysSinceJ2000(julianDay), "d");
  const lambda = toRadians(eclipticLongitude(n));
  const epsilon = toRadians(obliquity(n));
  return quantity(toDegrees(asin(sin(epsilon) * sin(lambda))), "deg");
}

/**
 * Équation du temps — l'écart entre midi solaire vrai et midi solaire moyen.
 * Positive quand le Soleil est en avance sur l'horloge.
 *
 * **Référence externe**, comme la déclinaison.
 */
export function equationOfTime(julianDay: Quantity<"time">): Quantity<"time"> {
  const n = valueIn(daysSinceJ2000(julianDay), "d");
  const lambda = toRadians(eclipticLongitude(n));
  const epsilon = toRadians(obliquity(n));
  const rightAscension = toDegrees(atan2(cos(epsilon) * sin(lambda), cos(lambda)));
  // 1° de rotation terrestre = 4 minutes de temps.
  return quantity(centerDegrees(meanLongitude(n) - rightAscension) * 4, "min");
}

/**
 * Angle horaire du Soleil : 0 au midi solaire vrai, négatif le matin, et
 * 15° par heure. Il combine l'heure UTC, la longitude du lieu et l'équation du
 * temps.
 */
export function hourAngle(
  julianDay: Quantity<"time">,
  longitude: Quantity<"angle">,
): Quantity<"angle"> {
  const jd = valueIn(julianDay, "d");
  // Le jour julien commence à midi : +0,5 ramène l'origine à minuit.
  const utcHours = (jd + 0.5 - floor(jd + 0.5)) * 24;
  const solarHours =
    utcHours +
    valueIn(longitude, "deg") / 15 +
    valueIn(equationOfTime(julianDay), "h");
  return quantity(centerDegrees((solarHours - 12) * 15), "deg");
}

/**
 * Instant du midi solaire vrai du jour contenant `julianDay`, au lieu donné.
 *
 * C'est l'inverse exact de `hourAngle` : celui-ci vaut zéro quand l'heure
 * solaire vaut 12, donc à `12 − longitude/15 − équation du temps`. L'équation
 * du temps dépend elle-même de l'instant, mais si faiblement qu'elle bouge de
 * quelques secondes sur une journée : deux passes suffisent à converger bien
 * en deçà de la seconde, ce qu'un banc vérifie.
 *
 * **Dépend de l'éphéméride, donc référence externe** — jamais un arbitre.
 *
 * Pourquoi ça compte : le protocole mesure l'ombre la plus courte de la
 * journée, et c'est celle-là. Calculer à midi UTC ferait varier l'ombre avec
 * la longitude pour une raison qui n'a rien de géométrique.
 */
export function solarNoon(
  julianDay: Quantity<"time">,
  longitude: Quantity<"angle">,
): Quantity<"time"> {
  const jd = valueIn(julianDay, "d");
  const midnight = floor(jd + 0.5) - 0.5;
  const degrees = valueIn(longitude, "deg");

  let noon = quantity(midnight + 0.5, "d");
  for (let pass = 0; pass < 2; pass += 1) {
    const hours = 12 - degrees / 15 - valueIn(equationOfTime(noon), "h");
    noon = quantity(midnight + hours / 24, "d");
  }
  return noon;
}

export interface ElevationInput {
  readonly latitude: Quantity<"angle">;
  readonly declination: Quantity<"angle">;
  readonly hourAngle: Quantity<"angle">;
}

/**
 * Élévation du Soleil au-dessus de l'horizon.
 *
 * `sin a = sin φ · sin δ + cos φ · cos δ · cos H`
 *
 * Pure géométrie sphérique : aucune approximation, aucune donnée externe. À
 * midi solaire (`H = 0`) elle se réduit à `a = 90° − φ + δ`, ce qui rend ses
 * bancs exacts par construction.
 */
export function solarElevation(input: ElevationInput): Quantity<"angle"> {
  const phi = toRadians(valueIn(input.latitude, "deg"));
  const delta = toRadians(valueIn(input.declination, "deg"));
  const h = toRadians(valueIn(input.hourAngle, "deg"));
  const sinElevation = sin(phi) * sin(delta) + cos(phi) * cos(delta) * cos(h);
  // Les arrondis peuvent pousser l'argument juste hors de [−1, 1] au zénith.
  const clamped = sinElevation > 1 ? 1 : sinElevation < -1 ? -1 : sinElevation;
  return quantity(toDegrees(asin(clamped)), "deg");
}

/** Élévation du Soleil pour un lieu et un instant donnés. */
export function solarElevationAt(
  julianDay: Quantity<"time">,
  latitude: Quantity<"angle">,
  longitude: Quantity<"angle">,
): Quantity<"angle"> {
  return solarElevation({
    latitude,
    declination: solarDeclination(julianDay),
    hourAngle: hourAngle(julianDay, longitude),
  });
}
